import React, { useEffect, useState } from 'react';
import {
  getExperts,
  createExpert,
  updateExpert,
  deleteExpert,
  downloadResume,
} from '../services/expert.service';
import ExpertForm from '../components/ExpertForm';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Avatar,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

const ExpertList: React.FC = () => {
  const [experts, setExperts] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordAlert, setPasswordAlert] = useState<{
    show: boolean;
    password: string;
    email: string;
  }>({ show: false, password: '', email: '' });

  const fetchExperts = () => {
    getExperts().then(res => setExperts(res.data.experts || []));
  };

  useEffect(() => {
    fetchExperts();
  }, []);

  const handleCreate = () => {
    setSelected(null);
    setOpen(true);
  };

  const handleEdit = (expert: any) => {
    setSelected(expert);
    setOpen(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this expert?')) {
      deleteExpert(id).then(fetchExperts);
    }
  };

  const handleSubmit = (data: any) => {
    setLoading(true);
    if (selected) {
      updateExpert(selected.id, data).then(response => {
        setOpen(false);
        setLoading(false);
        fetchExperts();

        // Show password alert if a new user was created with auto-generated password
        if (response.data.user_created && response.data.password) {
          setPasswordAlert({
            show: true,
            password: response.data.password,
            email: response.data.expert.email,
          });
        }
      });
    } else {
      createExpert(data).then(response => {
        setOpen(false);
        setLoading(false);
        fetchExperts();

        // Show password alert if a new user was created with auto-generated password
        if (response.data.user_created && response.data.password) {
          setPasswordAlert({
            show: true,
            password: response.data.password,
            email: response.data.expert.email,
          });
        }
      });
    }
  };

  const handleDownloadResume = async (expert: any) => {
    try {
      const response = await downloadResume(expert.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${expert.first_name}_${expert.last_name}_Resume.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading resume:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div>
      <Typography variant='h4' gutterBottom>
        Experts
      </Typography>
      <Button
        variant='contained'
        color='primary'
        onClick={handleCreate}
        sx={{ mb: 2 }}
      >
        Add Expert
      </Button>

      {passwordAlert.show && (
        <Alert
          severity='info'
          onClose={() =>
            setPasswordAlert({ show: false, password: '', email: '' })
          }
          sx={{ mb: 2 }}
        >
          <Typography variant='body2' gutterBottom>
            <strong>New user account created!</strong>
          </Typography>
          <Typography variant='body2' gutterBottom>
            Email: {passwordAlert.email}
          </Typography>
          <Typography variant='body2'>
            Auto-generated password: <strong>{passwordAlert.password}</strong>
          </Typography>
          <Typography variant='caption' display='block' sx={{ mt: 1 }}>
            Please share these credentials with the expert. They can change
            their password after logging in.
          </Typography>
        </Alert>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Profile</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Date of Birth</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Resume</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {experts.map(expert => (
              <TableRow key={expert.id}>
                <TableCell>
                  {expert.profile_picture ? (
                    <Avatar
                      src={`/storage/${expert.profile_picture}`}
                      alt={expert.first_name}
                    />
                  ) : (
                    <Avatar>{expert.first_name?.charAt(0)}</Avatar>
                  )}
                </TableCell>
                <TableCell>
                  <div>
                    <strong>
                      {expert.first_name} {expert.middle_name}{' '}
                      {expert.last_name}
                    </strong>
                    <br />
                    <small style={{ color: '#666' }}>{expert.address}</small>
                  </div>
                </TableCell>
                <TableCell>
                  {expert.date_of_birth
                    ? formatDate(expert.date_of_birth)
                    : '-'}
                </TableCell>
                <TableCell>{expert.phone_number}</TableCell>
                <TableCell>{expert.email}</TableCell>
                <TableCell>{expert.job_title || '-'}</TableCell>
                <TableCell>
                  {expert.gender
                    ? expert.gender.charAt(0).toUpperCase() +
                      expert.gender.slice(1)
                    : '-'}
                </TableCell>
                <TableCell>
                  {expert.resume_file_path ? (
                    <Button
                      variant='outlined'
                      size='small'
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadResume(expert)}
                    >
                      Download Resume
                    </Button>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(expert)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(expert.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>{selected ? 'Edit Expert' : 'Add Expert'}</DialogTitle>
        <DialogContent>
          <ExpertForm
            initialValues={selected || {}}
            onSubmit={handleSubmit}
            loading={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ExpertList;
